import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography, IconButton, TablePagination, Tooltip } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import MedicationIcon from '@mui/icons-material/Medication';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { useHistory } from 'react-router-dom';
import SectionCard from '../../../components/SectionCard';
import InvButton from '../../../components/InvButton';
import StatCard from '../../../components/StatCard';
import { InvTextField } from '../../../components/InvField';
import { getPatientPrescriptionDetails } from '../../../api/pharmacy';
import { formatDateTime } from '../../../utils';
import {
  buildPrescriptionLookupBody,
  isWithinOpdLookupWindow,
  lookupValidationMessage,
  mapOpdPatientEntries,
} from '../../../utils/opdPrescriptionEntity';
import { IPD_COLORS, cardSx } from '../../../theme';
import { fadeInSx, selectedRowSx } from '../pharmacyStyles';
import { PharmacyFilterBar, QuickActionCard } from '../PharmacyUi';
import PharmacyTableState from '../PharmacyTableState';
import OpdPrescriptionViewDialog from './OpdPrescriptionViewDialog';

const DEFAULT_ROWS_PER_PAGE = 10;
const TABLE_HEADERS = ['Date', 'Doctor', 'Facility', 'Diagnosis', 'Medicines', 'Actions'];

function patientSummaryLine(patient) {
  return [
    [patient.gender, patient.age].filter((v) => v !== '').join(' · '),
    patient.phone_no,
    patient.abha_id || patient.abha_no,
  ].filter(Boolean).join('  |  ');
}

/** Keep only prescriptions in the last 7 days; drop patients with none left. */
function filterEntriesToLookupWindow(entries) {
  return entries
    .map((entry) => ({
      ...entry,
      rows: entry.rows.filter((rx) => isWithinOpdLookupWindow(rx.datetime)),
    }))
    .filter((entry) => entry.rows.length > 0);
}

/**
 * OPD existing-patient search + prescription list (last 7 days).
 * @param {{ dispensePath: string }} props dispensePath — absolute or module-relative path to OpdDispenseFlow
 */
export default function OpdExistingPatientPanel({ dispensePath }) {
  const history = useHistory();

  const [query, setQuery] = useState('');
  const [resolvedQuery, setResolvedQuery] = useState('');
  const [queryError, setQueryError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientIndex, setPatientIndex] = useState(null);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [selected, setSelected] = useState(null);
  const [viewingRx, setViewingRx] = useState(null);

  const requestIdRef = useRef(0);
  useEffect(() => () => { requestIdRef.current += 1; }, []);

  const activePatient = patientIndex != null ? patients[patientIndex] : null;
  const rows = useMemo(() => activePatient?.rows || [], [activePatient]);

  const runSearch = useCallback(async () => {
    if (!buildPrescriptionLookupBody(query)) {
      setQueryError(lookupValidationMessage());
      return;
    }

    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;

    setResolvedQuery(query.trim());
    setQueryError(null);
    setLoading(true);
    setError(null);
    setSearched(true);
    setPatients([]);
    setPatientIndex(null);
    setSelected(null);
    setSearch('');

    try {
      const { patients: found } = await getPatientPrescriptionDetails(query);
      if (!isCurrent()) return;
      const entries = filterEntriesToLookupWindow(mapOpdPatientEntries(found));
      setPatients(entries);
      if (entries.length === 1) setPatientIndex(0);
    } catch (e) {
      if (!isCurrent()) return;
      setError(e?.message || 'Could not load prescriptions. Please try again.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [query]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((rx) => {
      if (!isWithinOpdLookupWindow(rx.datetime)) return false;
      if (!q) return true;
      return [rx.doctor_name, rx.facility_name, rx.diagnosis, ...rx.medications.map((m) => m.name)]
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage),
    [filtered, safePage, rowsPerPage],
  );

  useEffect(() => { setPage(1); }, [search, patientIndex]);

  const medicineTotal = useMemo(() => rows.reduce((sum, rx) => sum + rx.medicine_count, 0), [rows]);
  const doctorCount = useMemo(
    () => new Set(rows.map((rx) => rx.doctor_name).filter(Boolean)).size,
    [rows],
  );

  function startDispense(rx) {
    const next = new URLSearchParams({ q: resolvedQuery, rx: rx.id });
    history.push(`${dispensePath}?${next.toString()}`, { prescription: rx });
  }

  function changePatient() {
    setPatientIndex(null);
    setSelected(null);
  }

  return (
    <Box sx={fadeInSx}>
      <SectionCard
        title="Search patient"
        subtitle="Look up OPD prescriptions from the last 7 days by phone number or ABHA address"
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <InvTextField
              label="Phone number or ABHA address"
              value={query}
              error={queryError}
              onChange={(e) => { setQuery(e.target.value); if (queryError) setQueryError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            />
          </Box>
          <InvButton
            startIcon={<SearchIcon />}
            loading={loading}
            disabled={!query.trim()}
            onClick={runSearch}
            sx={{ mt: 3 }}
          >
            Search
          </InvButton>
        </Box>
      </SectionCard>

      {searched && !activePatient && (
        <Box sx={{ mt: 2.5 }}>
          <SectionCard title="Patients" subtitle="Select the patient whose prescriptions you want to dispense">
            <PharmacyTableState
              loading={loading}
              error={error}
              empty={!loading && !error && patients.length === 0}
              emptyMessage="No prescriptions found for this patient in the last 7 days."
              onRetry={runSearch}
            >
              <Grid container spacing={2}>
                {patients.map((entry, index) => (
                  <Grid item xs={12} md={6} key={entry.key}>
                    <QuickActionCard
                      icon={PersonIcon}
                      label={entry.patient.name || 'Unnamed patient'}
                      description={`${patientSummaryLine(entry.patient)} — ${entry.rows.length} prescription(s)`}
                      onClick={() => setPatientIndex(index)}
                    />
                  </Grid>
                ))}
              </Grid>
            </PharmacyTableState>
          </SectionCard>
        </Box>
      )}

      {activePatient && (
        <>
          <Box sx={{
            ...cardSx, p: 2, mt: 2.5, mb: 2.5,
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: IPD_COLORS.textPrimary }}>
                {activePatient.patient.name || 'Unnamed patient'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                {patientSummaryLine(activePatient.patient) || '—'}
              </Typography>
            </Box>
            {patients.length > 1 && (
              <InvButton variant="outlined" size="small" onClick={changePatient}>
                Change patient
              </InvButton>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6} sm={4}>
              <StatCard label="Prescriptions" value={rows.length} icon={AssignmentIndIcon} tone="teal" loading={loading} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard label="Medicines Prescribed" value={medicineTotal} icon={MedicationIcon} tone="mint" loading={loading} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard label="Doctors" value={doctorCount} icon={LocalHospitalIcon} tone="amber" loading={loading} />
            </Grid>
          </Grid>

          <PharmacyFilterBar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <InvTextField label="Search prescriptions" value={search} onChange={(e) => setSearch(e.target.value)} />
            </Box>
          </PharmacyFilterBar>

          <SectionCard
            title="OPD Prescriptions"
            subtitle={`${filtered.length} prescription(s) from the last 7 days`}
          >
            <PharmacyTableState
              loading={loading}
              error={error}
              empty={!loading && !error && filtered.length === 0}
              emptyMessage="No prescriptions in the last 7 days match your search."
              onRetry={runSearch}
            >
              <>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                        {TABLE_HEADERS.map((h) => (
                          <th key={h} scope="col" style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, color: IPD_COLORS.textSecondary, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((rx) => (
                        <tr
                          key={rx.id}
                          onClick={() => setSelected(rx)}
                          style={selectedRowSx(selected?.id === rx.id)}
                        >
                          <td style={{ padding: '12px', fontWeight: 600, color: IPD_COLORS.primary, whiteSpace: 'nowrap' }}>{formatDateTime(rx.datetime)}</td>
                          <td style={{ padding: '12px' }}>{rx.doctor_name || '—'}</td>
                          <td style={{ padding: '12px', fontSize: 13 }}>{rx.facility_name || '—'}</td>
                          <td style={{ padding: '12px', fontSize: 13 }}>{rx.diagnosis || '—'}</td>
                          <td style={{ padding: '12px' }}>{rx.medicine_count || '—'}</td>
                          <td style={{ padding: '12px' }}>
                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                              <InvButton size="small" startIcon={<PlayArrowIcon />}
                                onClick={() => startDispense(rx)}>
                                Start Dispense
                              </InvButton>
                              <Tooltip title="View prescription">
                                <IconButton
                                  size="small"
                                  sx={{ color: IPD_COLORS.primary }}
                                  aria-label={`View prescription from ${formatDateTime(rx.datetime)}`}
                                  onClick={() => setViewingRx(rx)}
                                >
                                  <RemoveRedEyeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>

                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={safePage - 1}
                  onPageChange={(e, next) => setPage(next + 1)}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                  sx={{
                    borderTop: `1px solid ${IPD_COLORS.border}`,
                    mt: 1,
                    '& .MuiTablePagination-toolbar': { px: 0 },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: 12,
                      color: IPD_COLORS.textSecondary,
                    },
                  }}
                />
              </>
            </PharmacyTableState>
          </SectionCard>
        </>
      )}

      <OpdPrescriptionViewDialog
        open={!!viewingRx}
        prescription={viewingRx}
        onClose={() => setViewingRx(null)}
        onStartDispense={startDispense}
      />
    </Box>
  );
}
